import { DEFAULT_PALETTE } from '../../core/palette';
import { POINT_DISTRIBUTION_OPTIONS } from '../../core/pointSources';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const SPACE_COLONIZATION_PARAMS = [
  {
    kind: 'int',
    key: 'attractors',
    label: 'Attraktoren',
    min: 50,
    max: 40000,
    step: 1,
    scale: 'log',
    default: 4000,
    bulkRange: [800, 14000],
    group: 'Wachstum',
    tooltip: 'Zielpunkte, zu denen das Adernetz wächst.',
  },
  {
    kind: 'enum',
    key: 'distribution',
    label: 'Verteilung',
    default: 'outside',
    group: 'Wachstum',
    options: POINT_DISTRIBUTION_OPTIONS,
  },
  {
    kind: 'float',
    key: 'distributionDistance',
    label: 'Reichweite',
    min: 5,
    max: 600,
    step: 1,
    default: 130,
    unit: 'px',
    group: 'Wachstum',
    showIf: (v) => v.distribution === 'ring' || v.distribution === 'nearText',
  },
  {
    kind: 'float',
    key: 'attractionDistance',
    label: 'Anziehungsdistanz',
    min: 10,
    max: 600,
    step: 1,
    scale: 'log',
    default: 95,
    unit: 'px',
    bulkRange: [40, 260],
    group: 'Wachstum',
    tooltip: 'Ab welcher Nähe ein Attraktor einen Ast beeinflusst.',
  },
  {
    kind: 'float',
    key: 'killDistance',
    label: 'Tötungsdistanz',
    min: 2,
    max: 120,
    step: 0.5,
    default: 13,
    unit: 'px',
    group: 'Wachstum',
    tooltip: 'Ab welcher Nähe ein Attraktor als erreicht gilt und verschwindet.',
  },
  {
    kind: 'float',
    key: 'segmentLength',
    label: 'Segmentlänge',
    min: 1,
    max: 40,
    step: 0.5,
    default: 6,
    unit: 'px',
    group: 'Wachstum',
  },
  {
    kind: 'enum',
    key: 'origin',
    label: 'Startpunkte',
    default: 'outline',
    group: 'Wachstum',
    options: [
      { value: 'outline', label: 'Textkontur' },
      { value: 'bottom', label: 'Unterer Rand' },
      { value: 'border', label: 'Alle Ränder' },
      { value: 'centre', label: 'Bildmitte' },
    ],
  },
  {
    kind: 'int',
    key: 'origins',
    label: 'Anzahl Startpunkte',
    min: 1,
    max: 500,
    step: 1,
    default: 90,
    group: 'Wachstum',
  },
  {
    kind: 'int',
    key: 'maxIterations',
    label: 'Max. Iterationen',
    min: 20,
    max: 3000,
    step: 10,
    default: 600,
    neverRandomise: true,
    group: 'Wachstum',
  },
  {
    kind: 'bool',
    key: 'avoidText',
    label: 'Text aussparen',
    default: true,
    group: 'Wachstum',
  },

  {
    kind: 'float',
    key: 'baseWidth',
    label: 'Stammbreite',
    min: 0.3,
    max: 30,
    step: 0.1,
    default: 5,
    unit: 'px',
    group: 'Darstellung',
  },
  {
    kind: 'float',
    key: 'widthFalloff',
    label: 'Verjüngung',
    min: 0.1,
    max: 1,
    step: 0.01,
    default: 0.62,
    group: 'Darstellung',
    tooltip: 'Wie schnell die Adern zu den Spitzen hin dünner werden.',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 0.92,
    group: 'Darstellung',
  },

  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'order',
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'order', label: 'Nach Astordnung' },
      { value: 'distance', label: 'Nach Textdistanz' },
      { value: 'time', label: 'Nach Wachstumszeit' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'moss' },
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type SpaceColonizationValues = SchemaValues<typeof SPACE_COLONIZATION_PARAMS>;
